import {
  useModals,
  Modal,
  ModalProps,
  Button,
  Checkbox,
  Skeleton,
  Stack,
} from "@skailer-com/uikit";
import { useBlogMyChannels, useBlogUpdateMyChannels } from "@/queries/blog.query.ts";
import { ApiBlogChannelShort } from "@/types/blog.types.ts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const getCheckedIds = (channels: ApiBlogChannelShort[] | undefined) =>
  channels?.reduce((acc: number[], channel) => {
    if (channel.is_subscribed || channel.is_subscribe_all_employees) acc.push(channel.id);

    return acc;
  }, []) ?? [];

export function BlogConfigureMyFeed(props: ModalProps) {
  const { id } = props;
  const { close } = useModals();
  const { data: channels, isLoading } = useBlogMyChannels();
  const { mutateAsync: saveChannels, isPending } = useBlogUpdateMyChannels();
  const { t } = useTranslation();
  const [checkedIds, setCheckedIds] = useState<number[]>(() => getCheckedIds(channels));
  const [syncedChannels, setSyncedChannels] = useState(channels);

  if (channels !== syncedChannels) {
    setSyncedChannels(channels);
    if (channels) setCheckedIds(getCheckedIds(channels));
  }

  const mandatoryChannelIds = useMemo(
    () =>
      channels
        ?.filter((channel) => channel.is_subscribe_all_employees)
        .map((channel) => channel.id) ?? [],
    [channels]
  );

  const handleSelect = (id: number) => {
    if (mandatoryChannelIds.includes(id)) return;

    setCheckedIds((prevIds) => {
      if (prevIds.includes(id)) return prevIds.filter((itemId) => itemId !== id);
      return [...prevIds, id];
    });
  };

  const handleSaveChannels = async () => {
    const channelIdsToSave = Array.from(new Set([...checkedIds, ...mandatoryChannelIds]));
    await saveChannels(channelIdsToSave);

    close(id);
  };

  return (
    <Modal
      title={t("blog:configure_my_feed")}
      withCloser={true}
      {...props}
      actions={
        <Stack>
          <Button
            variant={"primary"}
            size={"lg"}
            onClick={handleSaveChannels}
            isLoading={isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <Stack>
        {isLoading && (
          <Stack gap={"md"}>
            <Skeleton className={"h-5 w-40"} />
            <Skeleton className={"h-5 w-40"} />
            <Skeleton className={"h-5 w-40"} />
          </Stack>
        )}

        {channels && (
          <Stack gap={"sm"}>
            {channels.map((channel) => {
              const isMandatory = channel.is_subscribe_all_employees;

              return (
                <div
                  key={`configure-channel-${channel.id}`}
                  className={isMandatory ? "opacity-50" : ""}
                >
                  <Checkbox
                    checked={checkedIds.includes(channel.id)}
                    emoji={channel.emoji}
                    title={channel.title}
                    readOnly={isMandatory}
                    disabled={isMandatory}
                    caption={isMandatory ? t("blog:mandatory_channel_caption") : undefined}
                    onChange={() => {
                      handleSelect(channel.id);
                    }}
                  />
                </div>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
